import { useUser } from "@/hooks/queries/useUser";
import { authService } from "@/services";
import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "@/hooks/stores/useAuth";
import logo from "@/assets/images/logo.png";
import { useMobileMenu } from "./Navbar/Navbar"; // Import the mobile menu state
import { 
  LayoutDashboard, 
  Users, 
  UserCog,
  Scale,
  ScrollText,
  FileText,
  Video,
  LogOut,
  UserPlus,
  BarChart3
} from 'lucide-react';

const Sidebar = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { isOpen, close } = useMobileMenu(); // Add mobile menu state
  const sidebarRef = useRef(null);
  
  // Add check for registration page
  const isRegistrationPage = window.location.pathname === '/users/new';
  
  // Don't render sidebar on registration page
  if (isRegistrationPage) {
    return null;
  }

  // console.log('userRole', userRole);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/Login");
    } catch (error) {
      console.error("Logout failed:", error);
      // Still navigate to login page even if logout fails
      navigate("/Login");
    }
  };

  const { data: profile, isLoading, error } = useUser();

  // Improved click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        // Check if the click target is not the menu button
        const menuButton = document.querySelector('[aria-label="Toggle menu"]');
        if (!menuButton?.contains(event.target)) {
          close();
        }
      }
    };

    // Add event listener with capture phase to handle events early
    document.addEventListener('mousedown', handleClickOutside, true);
    
    // Cleanup function
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, close]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, close]);

  return (
    <aside 
      ref={sidebarRef}
      id="sidebar"
      className={`fixed left-0 lg:top-0 top-[4.6rem] w-full sm:w-[300px] bg-white lg:h-full h-[calc(100vh-4rem)] border-r border-border z-[60]
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
    >
      {/* Update overlay with fade animation */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 lg:hidden transition-opacity duration-300 ease-in-out z-[55]"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
        />
      )}
      
      <div className="flex flex-col h-full relative z-[65] bg-white">
        {/* Logo container - hidden on mobile, visible on desktop */}
        <div className="hidden lg:block p-4 sm:p-6">
          <img 
            src={logo} 
            alt="Post Scrypt Logo" 
            className="h-6 w-auto xs:h-8 sm:h-10 transition-all duration-200 cursor-pointer ml-auto mr-auto" 
            onClick={() => navigate('/')}
          />
          
          {/* Optional: Divider */}
          <div className="h-px bg-border w-full mt-4" />
        </div>

        {/* No padding needed since sidebar starts below navbar */}

        <div className="flex-1 overflow-y-auto ">
          <nav className="px-4 sm:px-[25px] pt-4 lg:pt-0">
            <ul>
              {userRole?.toLowerCase() === "admin" && (
                <>
                  <NavItem
                    link="/"
                    icon={
                      <LayoutDashboard 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Admin Dashboard"
                    onClick={() => navigate('/')}
                  />
                  
                  <NavItem
                    link="/Users"
                    icon={
                      <Users 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Users"
                    onClick={() => navigate('/Users')}
                  />
                  <NavItem
                    link="/head_of_department"
                    icon={
                      <UserCog 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="HOD"
                    onClick={() => navigate('/head_of_department')}
                  />
                  <NavItem
                    link="/advisors"
                    icon={
                      <Scale 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Legal Advisors"
                    onClick={() => navigate('/advisors')}
                  />
                  <NavItem
                    link="/roles"
                    icon={
                      <UserPlus 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Roles"
                    onClick={() => navigate('/Roles')}
                  />
                  <NavItem
                    link="/reports"
                    icon={
                      <BarChart3
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Reports"
                    onClick={() => navigate('/reports')}
                  />
                  <NavItem
                    link="/submissions"
                    icon={
                      <ScrollText 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Submissions"
                    onClick={() => navigate('/submissions')}
                  />
                </>
              )}
              {userRole?.toLowerCase() === "legal advisor" && (
                <>
                  <div className="mt-7 lg:mt-0"></div>
                  <NavItem
                    link="/"
                    icon={
                      <LayoutDashboard 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Legal Dashboard"
                    onClick={() => navigate('/')}
                  />
                  <NavItem
                    link="/submissions"
                    icon={
                      <ScrollText 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Submissions"
                    onClick={() => navigate('/submissions')}
                  />
                </>
              )}
              {userRole?.toLowerCase() === "head of department" && (
                <>
                  <NavItem
                    link="/"
                    icon={
                      <LayoutDashboard 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="HOD Dashboard"
                    onClick={() => navigate('/')}
                  />
                  <NavItem
                    link="/advisors"
                    icon={
                      <UserCog 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Legal Advisors"
                    onClick={() => navigate('/advisors')}
                  />
                  <NavItem
                    link="/submissions"
                    icon={
                      <ScrollText 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Submissions"
                    onClick={() => navigate('/submissions')}
                  />
                </>
              )}
              {userRole?.toLowerCase() === "user" && (
                <>
                  <NavItem
                    link="/"
                    icon={
                      <LayoutDashboard 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="User Dashboard"
                    onClick={() => navigate('/')}
                  />
                  <NavItem
                    link="/form"
                    icon={
                      <FileText 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Booklet"
                    onClick={() => navigate('/form')}
                  />
                  <NavItem
                    link="/video"
                    icon={
                      <Video 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Introductory Video"
                    onClick={() => navigate('/video')}
                  />
                </>
              )}
               {userRole?.toLowerCase() === "reporting" && (
                <>
                  <NavItem
                    link="/reports"
                    icon={
                      <BarChart3
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Reports"
                    onClick={() => navigate('/reports')}
                  />
                </>
              )}
                 {(userRole?.toLowerCase() === "manager" || userRole?.toLowerCase() === "bereavement team") && (
                <>
                  <NavItem
                    link="/"
                    icon={
                      <LayoutDashboard 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Dashboard"
                    onClick={() => navigate('/')}
                  />
                    <NavItem
                    link="/Users"
                    icon={
                      <Users 
                        className="w-[18px] h-[18px] stroke-current" 
                        strokeWidth={1.5}
                      />
                    }
                    menu="Users"
                    onClick={() => navigate('/Users')}
                  />
                </>
              )}
              <NavItem
                onClick={handleLogout}
                link="/#"
                icon={
                  <LogOut 
                    className="w-[18px] h-[18px] stroke-current" 
                    strokeWidth={1.5}
                  />
                }
                menu="Log out"
              />
            </ul>
          </nav>
        </div>
        {/* 
        <div className="border-t border-border p-[30px] pb-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {profile?.user?.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  HR Manager
                </p>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </aside>
  );
};

export default Sidebar;

const NavItem = ({ menu, link, icon, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { close } = useMobileMenu();
  
  // Function to determine if the current route is active
  const isActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const formState = location.state;
    const linkLower = link.toLowerCase();

    // For Users route
    if (linkLower === '/users') {
      // Only active if we're on /users path AND not creating HOD or Legal Advisor
      if (currentPath === '/users/create') {
        return !formState?.role || (formState?.role !== 'Head of Department' && formState?.role !== 'Legal Advisor');
      }
      return currentPath === '/users';
    }

    // For Roles route
    if (linkLower === '/roles') {
      return currentPath.startsWith('/roles');
    }

    // For HOD route
    if (linkLower === '/head_of_department') {
      return currentPath === '/head_of_department' || 
             (currentPath === '/users/create' && formState?.role === 'Head of Department');
    }

    // For Advisors route
    if (linkLower === '/advisors') {
      return currentPath === '/advisors' || 
             (currentPath === '/users/create' && formState?.role === 'Legal Advisor');
    }

    // For dashboard route
    if (linkLower === '/') {
      return currentPath === '/';
    }

    // For all other routes
    return currentPath === linkLower;
  };

  const handleClick = (e) => {
    e.preventDefault();
    close();
    
    if (onClick) {
      onClick();
    } else if (link) {
      navigate(link);
    }
  };

  return (
    <li className="relative">
      <div
        onClick={handleClick}
        className={`relative flex w-full items-center rounded py-3 sm:py-[10px] px-3 sm:px-[15px] text-base font-medium transition-colors cursor-pointer active:scale-95
          ${isActive() 
            ? 'bg-[#F16745] text-white hover:bg-[#E55D3D]' 
            : 'text-gray-600 hover:bg-accent hover:text-gray-900'
          }`}
        role="button"
        tabIndex={0}
      >
        <span className="mr-3 sm:mr-[10px] transition-colors">{icon}</span>
        <span className="text-sm sm:text-base">{menu}</span>
      </div>
    </li>
  );
};

const DropdownItem = ({ link, menu }) => {
  return (
    <li>
      <a
        href={link}
        className="flex w-full items-center rounded py-[10px] px-[15px] text-base font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        {menu}
      </a>
    </li>
  );
};
